import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';

import './ProfileCard.scss';
import PhotoInput from '../PhotoInput';
import TranslationProvider from '../../../providers/TranslationProvider';
import { RgaMember } from '../../Rga';
import Link from '../../Link';
import { postUserWithAuth } from '../../../api/api';
import { setUserData } from '../../../reducers/userSlice';
import Logger from '../../../utils/logger';
import checkIfWidget from '../../../utils/checkIfWidget';
import { RESY_WEB_ENABLE_WIDGET_MENU_UPDATES } from '../../../constants/optimizelyExperiments';
import useExperiment from '../../Experiments/useExperiment';

export const PROFILE_CARD_PAGE_CONTEXT = {
  PROFILE: 'profile',
  RESERVATIONS: 'reservations',
};

const PLACEHOLDER_PROFILE_URL = 'https://s3.amazonaws.com/resy.com/images/user.jpg';

const accountRedirect = `${checkIfWidget() ? '#' : ''}/account/profile`;

const STAT_MAP = {
  joined_resy: {
    key: 'profile_card_joined_resy',
    fallback: 'Joined Resy',
    key_single: 'profile_card_joined_resy',
    fallback_single: 'Joined Resy',
  },
  reservations: {
    key: 'reservations',
    key_single: 'reservation',
    fallback: 'Reservations',
    fallback_single: 'Reservation',
  },
  notify_wins: {
    key: 'profile_card_notify_wins',
    key_single: 'profile_card_notify_win',
    fallback: 'Notify Wins',
    fallback_single: 'Notify Win',
  },
};

const ProfileCard = ({
  context,
  joinDate,
  reservations,
  notifyWins,
  isGDAUser,
  isMobile,
  imageURL = null,
  user,
}) => {
  const dispatch = useDispatch();
  const [profileImageFile, setProfileImageFile] = useState(imageURL);
  const [, menuUpdatesVariation] = useExperiment(RESY_WEB_ENABLE_WIDGET_MENU_UPDATES);
  const renderStats = (stat, name) => (
    <div className="stat">
      <span className="number">{stat}</span>
      <TranslationProvider translationKey={STAT_MAP[name][`key${+stat === 1 ? '_single' : ''}`]}>
        <span className="category">{STAT_MAP[name][`fallback${+stat === 1 ? '_single' : ''}`]}</span>
      </TranslationProvider>
    </div>
  );

  const onFileChange = async (image) => {
    const file = URL.createObjectURL(image);
    const payload = new FormData();
    payload.append('image', image);
    payload.append('first_name', user.firstName);
    payload.append('last_name', user.lastName);
    payload.append('mobile_number', user.mobileNumber);
    payload.append('em_address', user.emAddress);
    try {
      const userData = await postUserWithAuth(payload);
      dispatch(setUserData(userData));
      setProfileImageFile(file);
      window.parent.postMessage('onProfileImage', '*');
    } catch (error) {
      Logger.error('[ProfileCard]: error saving new profile image: ', error);
    }
  };

  const buttonProps = () => {
    if (!profileImageFile || profileImageFile === PLACEHOLDER_PROFILE_URL) {
      return {
        buttonText: 'Add Photo',
        buttonKey: 'add_photo_cta',
      };
    }
    return {
      buttonText: 'Edit',
      buttonKey: 'edit',
    };
  };

  const truncateName = (nameStr) => {
    if (nameStr.length > 30) {
      if (nameStr.includes(' ') || nameStr.includes('-')) {
        return nameStr;
      }

      return `${nameStr.slice(0, 27)}...`;
    }
    return nameStr;
  };

  const checkIsConsumerWebWidget = () => {
    const refererDomain = window?.document?.referrer;
    if (refererDomain && refererDomain.length > 0) {
      return !refererDomain.includes('resy.com');
    }
    return false;
  };

  return (
    <div className={cx('ProfileCard', { 'ProfileCard--gda': isGDAUser })}>
      <div className="ProfileCard__photo" data-dd-privacy="mask">
        <PhotoInput
          {...buttonProps()}
          canClickPhoto
          onFileChange={onFileChange}
          isGda={isGDAUser}
          isMobile={isMobile}
          url={profileImageFile || null}
          size="74"
        />
      </div>
      <div className="ProfileCard__bio-section">
        <div className="ProfileCard__name-row">
          <div className="ProfileCard__name-header">
            <h1 className="ProfileCard__name" data-dd-privacy="mask">
              {truncateName(user.firstName)}
              {' '}
              {truncateName(user.lastName)}
            </h1>
            {user.bio && <p>{user.bio}</p>}
            {isGDAUser && <RgaMember variant="small" />}
          </div>

          {context !== PROFILE_CARD_PAGE_CONTEXT.PROFILE && (
            (menuUpdatesVariation === 'on' || !checkIsConsumerWebWidget()) && (
              <Link
                href={accountRedirect}
                variant="button-secondary"
              >
                <TranslationProvider translationKey="profile_card_edit_profile">
                  <div>Edit Profile</div>
                </TranslationProvider>
              </Link>
            )
          )}
        </div>
        <div className="ProfileCard__stats-row">
          {renderStats(reservations, 'reservations')}
          {renderStats(notifyWins, 'notify_wins')}
          {renderStats(joinDate, 'joined_resy')}
        </div>
      </div>
    </div>
  );
};
ProfileCard.propTypes = {
  context: PropTypes.oneOf(Object.values(PROFILE_CARD_PAGE_CONTEXT)),
  isGDAUser: PropTypes.bool,
  isMobile: PropTypes.bool.isRequired,
  imageURL: PropTypes.string,
  matchMedia: PropTypes.shape({
    lg: PropTypes.bool,
  }),
  joinDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  notifyWins: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  reservations: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  user: PropTypes.shape({
    emAddress: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    mobileNumber: PropTypes.string.isRequired,
    bio: PropTypes.string,
  }).isRequired,
};

ProfileCard.defaultProps = {
  context: PROFILE_CARD_PAGE_CONTEXT.PROFILE,
  isGDAUser: false,
  imageURL: null,
  matchMedia: { lg: false },
  notifyWins: 0,
  reservations: 0,
};

export default ProfileCard;
